//region header
//--------------------------------------------------------------------------------------------------
//
//  Description: Wizard finalise page
//
//  Copyright:    © 2021 - 2026 Idox Software Limited.
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier            Issue# Description
//region Version 1.0.0.0
//    001            Sean Flook                 Initial Revision.
//    002   24.11.23 Sean Flook                 Moved Box to @mui/system.
//    003   11.03.24 Sean Flook           GLB12 Adjusted height to remove gap.
//endregion Version 1.0.0.0
//region Version 1.0.6.0
//    004   22.07.25 Sean Flook       IMANN-1855 Use the new ADSCircularProgress component.
//endregion Version 1.0.6.0
//region Version 1.0.7.0
//    005   13.06.25 Sean Flook       IMANN-1812 Set the initial extent for the map.
//    006   27.10.25 Sean Flook       IMANN-2092 Use the new spatialReferenceWkid property from mapContext to set the spatialReference used by the layers in the map.
//    007   06.11.25 Sean Flook       IMANN-2092 Correctly set the spatialReferenceWkid variable.
//    008   17.12.25 Sean Flook       IMANN-2196 Added startExtent to prevent the map from continually zooming out.
//    009   16.03.26 Sean Flook       IMANN-2327 Pass through the onMapUpdated function to prevent the Finish button from being enabled prematurely.
//endregion Version 1.0.7.0
//
//--------------------------------------------------------------------------------------------------
//endregion header

import React, { useContext, useRef } from "react";
import PropTypes from "prop-types";

import MapContext from "../context/mapContext";
import SettingsContext from "../context/settingsContext";

import { getAuthorityExtent } from "../utils/HelperUtils";

import { Box } from "@mui/material";
import ADSMapDialog from "../components/ADSMapDialog";
import ADSCircularProgress from "../components/ADSCircularProgress";

import { dataFormStyle } from "./../utils/ADSStyles";

WizardMapPlacementPage.propTypes = {
  startExtent: PropTypes.object,
  data: PropTypes.array.isRequired,
  placeOnMapData: PropTypes.object.isRequired,
  isChild: PropTypes.bool,
  isRange: PropTypes.bool,
  creating: PropTypes.bool,
  onPlaceOnMapDataChange: PropTypes.func.isRequired,
  onMapUpdated: PropTypes.func.isRequired,
};

function WizardMapPlacementPage({
  startExtent,
  data,
  placeOnMapData,
  isChild = false,
  isRange = false,
  creating = false,
  onPlaceOnMapDataChange,
  onMapUpdated,
}) {
  const mapContext = useContext(MapContext);
  const settingsContext = useContext(SettingsContext);

  const spatialReferenceWkid = useRef(mapContext.spatialReference || 27700);

  /**
   * Event to handle when the place on map data changes.
   *
   * @param {object} placeOnMapData The place on map data.
   */
  const handlePlaceOnMapDataChange = (placeOnMapData) => {
    if (onPlaceOnMapDataChange) onPlaceOnMapDataChange(placeOnMapData);
  };

  return (
    <Box id="wizard-map-placement-page" sx={{ ...dataFormStyle("ADSMapDialogWizard"), width: "100%" }}>
      <ADSMapDialog
        startExtent={
          startExtent
            ? startExtent
            : mapContext.currentExtent
              ? { ...mapContext.currentExtent, spatialReference: { wkid: spatialReferenceWkid.current } }
              : getAuthorityExtent(
                  settingsContext.authorityCode,
                  settingsContext.isScottish,
                  settingsContext.isWelsh,
                  spatialReferenceWkid.current
                )
        }
        data={data}
        placeOnMapData={placeOnMapData}
        isChild={isChild}
        isRange={isRange}
        displayPlaceOnMap
        onPlaceOnMapDataChange={handlePlaceOnMapDataChange}
        onMapUpdated={onMapUpdated}
      />
      {creating && <ADSCircularProgress display={creating} />}
    </Box>
  );
}

export default WizardMapPlacementPage;
